<template>
  <b-modal
    :id="modalId"
    centered
    title="Update Summary"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="lg"
  >
    <div class="p-2 d-flex flex-column gap-4">
      <!-- Course Info -->
      <div
        class="rounded px-4 py-2 d-flex flex-row justify-content-between align-items-center"
        style="background-color: #fafafa"
      >
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>Date</span>
          <span>{{ data?.date }}</span>
        </div>
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>Start Time</span>
          <span>{{ formatTimeToHHMM(data?.start_time) }}</span>
        </div>
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>End Time</span>
          <span>{{ formatTimeToHHMM(data?.end_time) }}</span>
        </div>
      </div>

      <b-form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group label="Summary" :state="getState('summary')" invalid-feedback="Summary is required.">
          <b-form-textarea rows="2" v-model="form.summary" placeholder="Summary" :state="getState('summary')" />
        </b-form-group>

        <b-form-group label="Follow Up" :state="getState('follow_up')" invalid-feedback="Follow Up is required.">
          <b-form-textarea rows="2" v-model="form.follow_up" placeholder="Follow Up" :state="getState('follow_up')" />
        </b-form-group>

        <!-- <b-form-group label="Feedback" description="Read Only" tooltip>
          <b-form-textarea rows="2" v-model="form.feedback" placeholder="Feedback" disabled />
        </b-form-group> -->

        <b-form-group label="Attachment" :state="getState('attachments')" invalid-feedback="Attachment is required.">
          <p class="text-muted small mb-2">
            Upload your attachment in image or PDF format (JPG, JPEG, PNG, PDF). Max file size: 2MB
          </p>

          <div
            class="dropzone p-4 border rounded text-center"
            :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <div v-if="!selectedFile && !previewUrl">
              <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem"></i>
              <p class="text-muted mb-2">Drag & drop your attachment here or</p>
              <label class="btn btn-outline-danger btn-sm">
                Browse Files
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  @change="handleFileSelect"
                  hidden
                />
              </label>
            </div>

            <div v-else>
              <img
                v-if="previewUrl && !isPdf"
                :src="previewUrl"
                alt="Certificate Preview"
                class="img-fluid rounded mb-3"
                style="max-height: 250px"
              />
              <div v-else-if="isPdf" class="pdf-preview mb-3">
                <div class="pdf-icon-container">
                  <i class="fe fe-file-text"></i>
                </div>
                <p class="pdf-label mb-0">PDF File</p>
              </div>
              <div class="d-flex align-items-center justify-content-center gap-2">
                <span class="text-success">
                  <i class="fe fe-check-circle me-1"></i>
                  {{ selectedFile?.name || "N/A" }}
                </span>
                <button class="btn btn-outline-danger btn-sm" @click="removeFile">
                  <i class="fe fe-x"></i>
                </button>
              </div>
              <p class="text-muted small mt-2 mb-0">Size: {{ selectedFile ? formatFileSize(selectedFile?.size) : "N/A" }}</p>
            </div>
          </div>
          <small v-if="!selectedFile && submitted" class="text-danger"> Please select a attachment file </small>
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="isLoading" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Updating..." : "Update" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatTimeToHHMM, isPdfFile } from "../utils/utils";

export default {
  name: "UpdateSummaryModal",

  props: {
    modalId: { type: String, default: "update-summary-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.getInitialForm(),
      selectedFile: null,
      previewUrl: null,
      isPdf: false,
      isDragging: false,
      submitted: false,
      formErrors: {},
      suppressValidation: false,
    };
  },

  watch: {
    data: {
      deep: true,
      handler(val) {
        this.populateFromData(val);
      },
    },
  },

  methods: {
    formatTimeToHHMM,
    isPdfFile,

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.summary) this.formErrors.summary = "Summary is required.";

      if (!this.form.follow_up) this.formErrors.follow_up = "Follow Up is required.";

      if (!this.form.attachments) this.formErrors.attachments = "Attachment is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = {
        id: this.form.id,
        summary: this.form.summary,
        follow_up: this.form.follow_up,
      };

      if (typeof this.form.attachments === "object") payload.attachments = this.form.attachments;

      this.$emit("update-summary", payload);
    },

    handleChangeFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.form.attachments = file;
      this.form.id = this.data.id;
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    processFile(file) {
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        this.$swal({
          title: "Tipe File Tidak Valid",
          text: "Unggah file gambar (JPG, JPEG, PNG) atau PDF",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: "File Terlalu Besar",
          text: "Ukuran file maksimal 2MB",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      this.selectedFile = file;
      this.isPdf = file.type === "application/pdf";

      this.form.attachments = file;

      if (!this.isPdf) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }

      this.formErrors.attachments = null;
    },

    removeFile() {
      this.selectedFile = null;
      this.isPdf = false;
      this.form.attachments = null;

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    getInitialForm() {
      return {
        summary: null,
        follow_up: null,
        feedback: null,
        attachments: [],
        id: null,
      };
    },

    populateFromData(d) {
      this.form = {
        summary: d.coaching_summary.summary,
        follow_up: d.coaching_summary.follow_up,
        feedback: d.coaching_summary.feedback,
        attachments: d.coaching_summary.attachments,
        id: d.coaching_summary.id,
      };

      if (d.coaching_summary.attachments) this.previewUrl = d.coaching_summary.attachments;

      this.isPdf = isPdfFile(d.coaching_summary.attachments);

      this.formErrors = {};
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      if (this.data) {
        this.populateFromData(this.data);
        this.previewUrl = this.data.coaching_summary.attachments;
        this.selectedFile = null;
      } else {
        this.form = this.getInitialForm();
        this.formErrors = {};
        this.selectedFile = null;
        this.previewUrl = null;
      }

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
